import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Camera, Upload, FileText, Loader2, Check, RefreshCw, Save } from 'lucide-react';
import { toast } from 'sonner';

// Helper to extract error message from various error formats
const getErrorMessage = (error, fallback = 'An error occurred') => {
  const detail = error?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    // Pydantic validation errors format
    return detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
  }
  if (typeof detail === 'object' && detail.msg) return detail.msg;
  return fallback;
};

const ReceiptScanner = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  
  // For transaction creation
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  
  // Load accounts and categories on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        const [accountsRes, categoriesRes] = await Promise.all([
          api.get('/accounts'),
          api.get('/categories')
        ]);
        
        // Filter for payment accounts (cash/bank)
        const paymentAccounts = accountsRes.data.filter(a => 
          a.account_subtype === 'cash' || a.account_subtype === 'bank'
        );
        setAccounts(paymentAccounts);
        
        // Filter for expense categories
        const expenseCategories = categoriesRes.data.filter(c => 
          c.category_type === 'expense' || c.category_type === 'both'
        );
        setCategories(expenseCategories);
        
        // Set defaults
        if (paymentAccounts.length > 0) {
          setSelectedAccount(paymentAccounts[0].id);
        }
      } catch (error) {
        console.error('Failed to load accounts/categories', error);
      }
    };
    loadData();
  }, []);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a JPEG, PNG, or WEBP image');
      return;
    }
    
    // Preview
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target.result);
    reader.readAsDataURL(file);
    
    // Process
    setLoading(true);
    setExtractedData(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await api.post('/ai/scan-receipt-file', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (res.data.success) {
        setExtractedData(res.data);
        toast.success('Receipt scanned successfully!');
      } else {
        toast.error(res.data.error || 'Failed to scan receipt');
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to scan receipt'));
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setImagePreview(null);
    setExtractedData(null);
    setSelectedCategory('');
  };

  const createTransaction = async () => {
    if (!extractedData) return;
    
    if (!selectedAccount) {
      toast.error('Please select a payment account');
      return;
    }
    
    if (!extractedData.total_amount) {
      toast.error('No amount extracted from receipt');
      return;
    }
    
    setSaving(true);
    try {
      const transactionData = {
        transaction_type: 'expense',
        amount: parseFloat(extractedData.total_amount),
        date: extractedData.date || new Date().toISOString().split('T')[0],
        payment_account_id: selectedAccount,
        category_id: selectedCategory || null,
        description: extractedData.vendor_name || 'Receipt expense',
        notes: extractedData.items 
          ? `Items: ${extractedData.items.map(i => `${i.description} ($${i.amount})`).join(', ')}`
          : 'Created from receipt scan',
        payment_method: extractedData.payment_method || 'card'
      };
      
      await api.post('/transactions', transactionData);
      
      toast.success('Transaction created successfully!');
      reset();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create transaction'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="receipt-scanner-page">
      <div>
        <h1 className="text-2xl font-bold text-[hsl(var(--ink-1))]" style={{ fontFamily: 'Space Grotesk' }}>
          Receipt Scanner
        </h1>
        <p className="text-[hsl(var(--muted-ink))]">
          Scan receipts with AI to automatically extract transaction data
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Upload Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Camera size={20} />
              Scan Receipt
            </CardTitle>
            <CardDescription>
              Upload a photo of your receipt
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!imagePreview ? (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <p className="font-medium mb-2">Upload Receipt Image</p>
                <p className="text-sm text-[hsl(var(--muted-ink))] mb-4">JPEG, PNG, or WEBP</p>
                <Input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleFileUpload}
                  className="max-w-xs mx-auto"
                  data-testid="receipt-file-input"
                />
              </div>
            ) : (
              <div className="space-y-4">
                <div className="relative">
                  <img 
                    src={imagePreview} 
                    alt="Receipt preview" 
                    className="max-h-96 mx-auto rounded-lg shadow-md"
                  />
                  {loading && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-lg">
                      <div className="text-white text-center">
                        <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
                        <p>Scanning receipt...</p>
                      </div>
                    </div>
                  )}
                </div>
                <Button variant="outline" onClick={reset} className="w-full">
                  <RefreshCw size={16} className="mr-2" />
                  Scan Another Receipt
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Results Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText size={20} />
              Extracted Data
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!extractedData && !loading && (
              <div className="text-center py-12 text-[hsl(var(--muted-ink))]">
                <FileText className="mx-auto h-12 w-12 mb-4" />
                <p>Upload a receipt to see extracted data</p>
              </div>
            )}
            
            {loading && (
              <div className="text-center py-12">
                <Loader2 className="mx-auto h-8 w-8 animate-spin text-[hsl(var(--brand-500))]" />
                <p className="mt-4 text-[hsl(var(--muted-ink))]">AI is analyzing your receipt...</p>
              </div>
            )}
            
            {extractedData && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-green-600 mb-4">
                  <Check size={20} />
                  <span className="font-medium">Receipt scanned successfully!</span>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-[hsl(var(--muted-ink))]">Vendor</span>
                    <span className="font-medium">{extractedData.vendor_name || '-'}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-[hsl(var(--muted-ink))]">Date</span>
                    <span className="font-medium">{extractedData.date || '-'}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-[hsl(var(--muted-ink))]">Subtotal</span>
                    <span className="font-medium">
                      {extractedData.subtotal ? `${extractedData.currency || '$'}${extractedData.subtotal}` : '-'}
                    </span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-[hsl(var(--muted-ink))]">Tax</span>
                    <span className="font-medium">
                      {extractedData.tax_amount ? `${extractedData.currency || '$'}${extractedData.tax_amount}` : '-'}
                    </span>
                  </div>
                  <div className="flex justify-between py-2 border-b text-lg">
                    <span className="font-medium">Total</span>
                    <span className="font-bold text-[hsl(var(--brand-600))]">
                      {extractedData.total_amount ? `${extractedData.currency || '$'}${extractedData.total_amount}` : '-'}
                    </span>
                  </div>
                  {extractedData.payment_method && (
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-[hsl(var(--muted-ink))]">Payment Method</span>
                      <span className="font-medium capitalize">{extractedData.payment_method}</span>
                    </div>
                  )}
                </div>
                
                {extractedData.items && extractedData.items.length > 0 && (
                  <div className="mt-4">
                    <Label className="mb-2 block">Line Items</Label>
                    <div className="bg-gray-50 rounded-lg p-3 space-y-2 max-h-40 overflow-y-auto">
                      {extractedData.items.map((item, i) => (
                        <div key={i} className="flex justify-between text-sm">
                          <span>{item.description}</span>
                          <span>${item.amount}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Transaction Details for Saving */}
                <div className="mt-6 pt-4 border-t space-y-4">
                  <h4 className="font-medium">Save as Transaction</h4>
                  
                  <div>
                    <Label>Payment Account *</Label>
                    <Select value={selectedAccount} onValueChange={setSelectedAccount}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select account" />
                      </SelectTrigger>
                      <SelectContent>
                        {accounts.map(a => (
                          <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label>Category (optional)</Label>
                    <Select value={selectedCategory || '_none'} onValueChange={(v) => setSelectedCategory(v === '_none' ? '' : v)}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none">No category</SelectItem>
                        {categories.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="pt-4">
                  <Button 
                    onClick={createTransaction}
                    disabled={saving || !selectedAccount}
                    className="w-full bg-[hsl(var(--brand-500))] hover:bg-[hsl(var(--brand-600))] text-white"
                  >
                    {saving ? (
                      <>
                        <Loader2 size={16} className="mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save size={16} className="mr-2" />
                        Save Transaction
                      </>
                    )}
                  </Button>
                </div>
                
                <p className="text-xs text-center text-[hsl(var(--muted-ink))]">
                  AI Confidence: {Math.round((extractedData.confidence || 0) * 100)}%
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ReceiptScanner;
